import React from 'react';
import { format } from 'date-fns';
import BatchRenameModal from './BatchRenameModal';
import DuplicateUploadBanner from './DuplicateUploadBanner';
import { useProjectFileDrop } from '../hooks/useProjectFileDrop';
import { useAuth } from '../contexts/AuthContext';
import SparkleIcon from './icons/SparkleIcon';

export interface Project {
  id: string;
  userId: string;
  customerId: string;
  parentId?: string | null; // Self-reference for nested sub-folders
  title: string;
  description?: string;
  type: 'new_business' | 'renewal' | 'loss_run' | 'year_over_year' | 'comparison' | 'insurance_requirements' | 'other';
  status: 'planning' | 'active' | 'completed' | 'on_hold' | 'cancelled';
  startDate: Date;
  endDate?: Date;
  targetCompletionDate?: Date;
  renewalDate?: Date;
  metadata?: any;
  createdAt: Date;
  updatedAt: Date;
  createdByName?: string; // Creator attribution (surfaced for shared customers)
  createdByEmail?: string;
  isOwner?: boolean;
  analytics?: {
    documentCount: number;
    chatCount: number;
    reportCount: number;
    workflowCount: number;
    completionPercentage: number;
  };
}

interface ProjectCardProps {
  project: Project;
  onClick: (project: Project) => void;
  onEdit?: (project: Project) => void;
  onDelete?: (project: Project) => void;
  selected?: boolean;
  onDocumentsUploaded?: (project: Project, count: number) => void;
  /** Number of direct sub-folders; shows a drill-in affordance when > 0. */
  childCount?: number;
  /** Drill into this folder's sub-folders (distinct from opening its workspace). */
  onOpenSubfolders?: (project: Project) => void;
  /** Create a sub-folder inside this folder. */
  onAddSubfolder?: (project: Project) => void;
  /** Recommended-workflow count for this project (0 renders no badge). */
  suggestionCount?: number;
  /** Open this project directly on its Workflows tab (badge click target). */
  onOpenWorkflows?: (project: Project) => void;
}

const typeConfig = {
  new_business: {
    label: 'New Business',
    color: 'bg-blue-500',
    icon: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z'
  },
  renewal: {
    label: 'Renewal',
    color: 'bg-green-500',
    icon: 'M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z'
  },
  loss_run: {
    label: 'Loss Run',
    color: 'bg-red-500',
    icon: 'M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z'
  },
  year_over_year: {
    label: 'Year Over Year',
    color: 'bg-purple-500',
    icon: 'M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z'
  },
  insurance_requirements: {
    label: 'Insurance Requirements',
    color: 'bg-indigo-500',
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01'
  },
  comparison: {
    label: 'Comparison',
    color: 'bg-orange-500',
    icon: 'M9 3L5 6.99h3V14h2V6.99h3L9 3zm7 14.01V10h-2v7.01h-3L15 21l4-3.99h-3z'
  },
  other: {
    label: 'Other',
    color: 'bg-gray-500',
    icon: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z'
  }
};

const statusConfig = {
  planning: { label: 'Planning', color: 'text-yellow-600 bg-yellow-50' },
  active: { label: 'Active', color: 'text-green-600 bg-green-50' },
  completed: { label: 'Completed', color: 'text-blue-600 bg-blue-50' },
  on_hold: { label: 'On Hold', color: 'text-orange-600 bg-orange-50' },
  cancelled: { label: 'Cancelled', color: 'text-red-600 bg-red-50' }
};

const ProjectCard: React.FC<ProjectCardProps> = ({ project, onClick, onEdit, onDelete, selected = false, onDocumentsUploaded, childCount = 0, onOpenSubfolders, onAddSubfolder, suggestionCount = 0, onOpenWorkflows }) => {
  const typeInfo = typeConfig[project.type];
  const statusInfo = statusConfig[project.status];

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.(project);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.(project);
  };

  const { token } = useAuth();
  const { dragProps, isDragging, uploading, uploadError, duplicateNotice, dismissDuplicateNotice, renameModalProps } = useProjectFileDrop({
    project,
    onDocumentsUploaded: (count) => onDocumentsUploaded?.(project, count),
  });

  return (
    <div
      onClick={() => onClick(project)}
      {...dragProps}
      className={`
        group relative p-6 rounded-lg border-2 cursor-pointer transition-all
        ${selected
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
          : isDragging
          ? 'border-primary border-dashed bg-blue-50 dark:bg-blue-900/20'
          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
        }
      `}
    >
      {/* Drop overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-blue-50/95 dark:bg-blue-900/40 rounded-lg pointer-events-none">
          <svg className="w-8 h-8 text-primary mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.9 5 5 0 119.9 1H21a3 3 0 010 6h-1m-5 1l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <span className="text-sm font-medium text-primary">Drop to upload to {project.title}</span>
        </div>
      )}

      {/* Uploading progress */}
      {uploading && (
        <div className="absolute inset-x-0 bottom-0 z-20 px-3 py-2 bg-blue-600 text-white text-xs rounded-b-lg flex items-center justify-between pointer-events-none">
          <span>Uploading {uploading.done}/{uploading.total}…</span>
          <div className="w-24 h-1.5 bg-blue-900/40 rounded-full overflow-hidden">
            <div className="h-full bg-white" style={{ width: `${(uploading.done / uploading.total) * 100}%` }} />
          </div>
        </div>
      )}

      {/* Error toast */}
      {uploadError && (
        <div className="absolute inset-x-0 bottom-0 z-20 px-3 py-2 bg-red-600 text-white text-xs rounded-b-lg pointer-events-none">
          {uploadError}
        </div>
      )}
      {/* Duplicate-upload notice — non-blocking, the upload still succeeded.
          No "View existing" here — there's no document list in this view. */}
      {!uploadError && duplicateNotice && (
        <div className="absolute inset-x-0 bottom-0 z-20 rounded-b-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
          <DuplicateUploadBanner
            notice={duplicateNotice}
            token={token}
            className="mb-0"
            onDismiss={dismissDuplicateNotice}
            onRemoved={() => {
              dismissDuplicateNotice();
              onDocumentsUploaded?.(project, 0);
            }}
          />
        </div>
      )}
      {/* Top Right Section */}
      <div className="absolute top-4 right-4 flex flex-col items-end space-y-2">
        {/* Type Badge */}
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-white ${typeInfo.color}`}>
          {typeInfo.label}
        </span>

        {/* Action Buttons */}
        {(onEdit || onDelete || onAddSubfolder) && (
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="flex items-center space-x-1 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-600 p-1">
              {onAddSubfolder && (
                <button
                  onClick={(e) => { e.stopPropagation(); onAddSubfolder(project); }}
                  className="p-1.5 text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                  title="Add sub-folder"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11v4M10 13h4" />
                  </svg>
                </button>
              )}
              {onEdit && (
                <button
                  onClick={handleEditClick}
                  className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                  title="Edit project"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              )}
              {onDelete && (
                <button
                  onClick={handleDeleteClick}
                  className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                  title="Delete project"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Project Title */}
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 pr-24">
        {project.title}
      </h3>

      {/* Description */}
      {project.description && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
          {project.description}
        </p>
      )}

      {/* Status */}
      <div className="mb-3 flex items-center gap-2">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
          {statusInfo.label}
        </span>
        {/* Sub-folder count → drill in */}
        {childCount > 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); onOpenSubfolders?.(project); }}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
            title="Open sub-folders"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
            </svg>
            {childCount} {childCount === 1 ? 'sub-folder' : 'sub-folders'}
          </button>
        )}
        {suggestionCount > 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); onOpenWorkflows?.(project); }}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 transition-colors"
            title={`${suggestionCount} recommended workflow${suggestionCount === 1 ? '' : 's'}`}
          >
            <SparkleIcon className="w-3 h-3" />
            {suggestionCount}
          </button>
        )}
      </div>

      {/* Creator attribution — useful on shared customers */}
      {project.createdByName && (
        <div className="mb-2 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Created by {project.createdByName}
        </div>
      )}

      {/* Dates */}
      <div className="space-y-1 text-sm text-gray-500 dark:text-gray-400 mb-3">
        <div>Started: {format(new Date(project.startDate), 'MMM d, yyyy')}</div>
        {project.targetCompletionDate && (
          <div>Target: {format(new Date(project.targetCompletionDate), 'MMM d, yyyy')}</div>
        )}
        {project.renewalDate && (
          <div>Renewal: {format(new Date(project.renewalDate), 'MMM d, yyyy')}</div>
        )}
      </div>

      {/* Analytics Summary */}
      {project.analytics && (
        <div className="border-t pt-3 mt-3">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400">Documents:</span>
              <span className="ml-1 font-medium text-gray-900 dark:text-white">
                {project.analytics.documentCount}
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Chats:</span>
              <span className="ml-1 font-medium text-gray-900 dark:text-white">
                {project.analytics.chatCount}
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Reports:</span>
              <span className="ml-1 font-medium text-gray-900 dark:text-white">
                {project.analytics.reportCount}
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Workflows:</span>
              <span className="ml-1 font-medium text-gray-900 dark:text-white">
                {project.analytics.workflowCount}
              </span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-gray-500 dark:text-gray-400">Progress</span>
              <span className="text-gray-900 dark:text-white font-medium">
                {project.analytics.completionPercentage}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all"
                style={{ width: `${project.analytics.completionPercentage}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {renameModalProps && <BatchRenameModal {...renameModalProps} />}
    </div>
  );
};

export default ProjectCard;